import React, { useState, useEffect, useCallback, useRef, useMemo } from "react"
import { useSelector } from "react-redux"
import { CiExport } from "react-icons/ci"
import { IoSearchOutline } from "react-icons/io5"
import PatientTableComponent from "../../NurseAssignments/PatientTable/PatientTableComponent"
import PatientFilter from "../../NurseAssignments/PatientTable/PatientFilter"
import ColumnSelector from "../../NurseAssignments/PatientTable/ColumnSelector"
import {
	handleExport,
	filterAndSortData as sharedFilterAndSortData,
	handleSearch as sharedHandleSearch
} from "../../NurseAssignments/utils/SharedListUtils"
import { getTimeForTable, endTimeAllCases } from "../../../utils/realTimeUtils"

export const getSeverityColor = severity => {
	switch (severity) {
		case "error":
			return "bg-red-100 text-red-800"
		case "warning":
			return "bg-yellow-100 text-yellow-800"
		case "info":
			return "bg-blue-100 text-blue-800"
		default:
			return "bg-gray-100 text-gray-800"
	}
}

const FuturePatientList = ({ patients, schedulingIssues = [] }) => {
	const sandbox = useSelector(state => state.sandbox.isSandboxMode)
	const isTomorrowMode = useSelector(state => state.tomorrow.isTomorrowMode)
	const resetPaginationRef = useRef(null)

	// State for filtering and searching
	const [filteredData, setFilteredData] = useState([])
	const [query, setQuery] = useState("")
	const [activeFilters, setActiveFilters] = useState([])
	const [filterRelationship, setFilterRelationship] = useState("AND")
	// eslint-disable-next-line no-unused-vars
	const [filters, setFilters] = useState({
		acuity: [],
		chair: [],
		startTime: "",
		endTime: "",
		lengthRange: [0, 12],
		progressRange: "",
		nurse: [],
		infusionType: [],
		reasonForVisit: [],
		referringDepartment: [],
		patientStatus: []
	})
	const [sortConfig, setSortConfig] = useState({ key: "", direction: "asc" })
	const [visibleColumns, setVisibleColumns] = useState(null)

	// Define available columns (matching other views but without nurse/chair, adding schedule issues)
	const availableColumns = useMemo(
		() => [
			{
				id: "patient",
				name: "Patient Name",
				field: "patientName",
				dataType: "string",
				default: true
			},
			{
				id: "startTime",
				name: "Start Time",
				field: "actualStartTime",
				dataType: "time",
				default: true
			},
			{
				id: "endTime",
				name: "End Time",
				field: "actualEndTime",
				dataType: "time",
				default: true
			},
			{
				id: "length",
				name: "Infusion Length",
				field: "length",
				dataType: "number",
				default: true
			},
			{
				id: "infusionType",
				name: "Infusion Type",
				field: "infusionType",
				dataType: "list",
				default: true
			},
			{
				id: "reasonForVisit",
				name: "Reason for Visit",
				field: "reasonForVisit",
				dataType: "string",
				default: true
			},
			{
				id: "referringDepartment",
				name: "Referring Department",
				field: "department",
				dataType: "list",
				default: true
			},
			{ id: "acuity", name: "Acuity", field: "acuity", dataType: "list", default: true },
			{
				id: "costInformation",
				name: "Cost Information",
				field: "costInformation",
				dataType: "list",
				default: true
			},
			{
				id: "scheduleIssues",
				name: "Schedule Issues",
				field: "scheduleIssuesText",
				dataType: "string",
				default: true
			},
			{
				id: "clinicAppointment",
				name: "Clinic Appointment Start Time",
				field: "clinicAppointmentTime",
				dataType: "time",
				default: false
			},
			{
				id: "arrivalTime",
				name: "Arrival Time",
				field: "arrivalTime",
				dataType: "time",
				default: false
			},
			{
				id: "bookedDate",
				name: "Booked Date",
				field: "bookedDate",
				dataType: "string",
				default: false
			},
			{
				id: "visitStatus",
				name: "Visit Status",
				field: "internalStatus",
				dataType: "list",
				default: false
			},
			{
				id: "clinicProvider",
				name: "Clinic Provider",
				field: "clinicProvider",
				dataType: "string",
				default: false
			},
			{
				id: "linkedClinicAppointment",
				name: "Clinic Appointment Type",
				field: "linkedClinicAppointment",
				dataType: "list",
				default: false
			},
			{ id: "notes", name: "Notes", field: "notes", dataType: "string", default: false }
		],
		[]
	)

	// Memoize the patient issues map to prevent recalculation on every render
	const patientIssuesMap = useMemo(() => {
		return schedulingIssues.reduce((map, issue) => {
			const patientIdDouble = parseFloat(issue.patientId)
			if (!isNaN(patientIdDouble)) {
				if (!map[patientIdDouble]) {
					map[patientIdDouble] = []
				}
				map[patientIdDouble].push(issue)
			}
			return map
		}, {})
	}, [schedulingIssues])

	// Memoize the patients with issues to prevent recalculation on every render
	const patientsWithIssues = useMemo(() => {
		return patients.map(patient => {
			const patientIssues = patientIssuesMap[patient.patientId] || []
			let scheduleIssuesText = "None"
			let scheduleIssuesArray = []

			if (patientIssues.length > 0) {
				const issueTypes = patientIssues.map(issue => issue.type || "Issue").join(", ")
				scheduleIssuesText = issueTypes
				scheduleIssuesArray = patientIssues
			}

			return {
				...patient,
				scheduleIssuesText: scheduleIssuesText,
				scheduleIssuesArray: scheduleIssuesArray
			}
		})
	}, [patients, patientIssuesMap])

	const filterData = useCallback(
		(queryString = "") => {
			let filtered = sharedFilterAndSortData(
				patientsWithIssues,
				queryString,
				filters,
				[], // nurseSchedule - empty for future patients
				sortConfig
			)

			// Apply advanced filters if they exist
			if (activeFilters.length > 0) {
				filtered = applyAdvancedFilters(filtered, activeFilters, filterRelationship)
			}

			setFilteredData(filtered)
		},
		// `applyAdvancedFilters` is stable within this component context.
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[patientsWithIssues, filters, activeFilters, sortConfig, filterRelationship]
	)

	// Apply the advanced filters from PatientFilter component
	const applyAdvancedFilters = useCallback((data, filters, relationship = "AND") => {
		if (!filters.length) return data

		return data.filter(item => {
			if (relationship === "AND") {
				// All conditions must match (AND)
				return filters.every(filter => evaluateFilter(item, filter))
			} else {
				// Any condition can match (OR)
				return filters.some(filter => evaluateFilter(item, filter))
			}
		})
		// `evaluateFilter` is stable within this component context.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])

	// Evaluate a single filter against an item
	const evaluateFilter = useCallback(
		(item, filter) => {
			// Handle calculation filter type (time difference)
			if (filter.type === "calculation") {
				let time1, time2

				// Use appropriate time values based on column ID
				if (filter.field1 === "startTime") {
					time1 = getTimeForTable(item)
				} else if (filter.field1 === "endTime") {
					time1 = endTimeAllCases(item, sandbox, isTomorrowMode)
				} else {
					// For other time fields, use the field value directly
					const column = availableColumns.find(c => c.id === filter.field1)
					time1 = column ? parseInt(item[column.field] || 0) : 0
				}

				if (filter.field2 === "startTime") {
					time2 = getTimeForTable(item)
				} else if (filter.field2 === "endTime") {
					time2 = endTimeAllCases(item, sandbox, isTomorrowMode)
				} else {
					// For other time fields, use the field value directly
					const column = availableColumns.find(c => c.id === filter.field2)
					time2 = column ? parseInt(item[column.field] || 0) : 0
				}

				const difference = Math.abs(time1 - time2)
				const compareValue = parseInt(filter.value || 0)

				switch (filter.operator) {
					case "greater_than":
						return (
							!isNaN(difference) && !isNaN(compareValue) && difference > compareValue
						)
					case "less_than":
						return (
							!isNaN(difference) && !isNaN(compareValue) && difference < compareValue
						)
					case "equals":
						return (
							!isNaN(difference) &&
							!isNaN(compareValue) &&
							difference === compareValue
						)
					default:
						return true
				}
			}

			// Handle standard filters
			const column = availableColumns.find(c => c.id === filter.property)
			if (!column) return true

			const fieldValue = item[column.field]

			switch (filter.operator) {
				case "equals":
					if (column.dataType === "boolean") {
						return String(fieldValue) === filter.value
					}
					return fieldValue === filter.value // Using loose equality for type conversion

				case "contains":
					return (
						fieldValue &&
						String(fieldValue)
							.toLowerCase()
							.includes(String(filter.value).toLowerCase())
					)

				case "starts_with":
					return (
						fieldValue &&
						String(fieldValue)
							.toLowerCase()
							.startsWith(String(filter.value).toLowerCase())
					)

				case "ends_with":
					return (
						fieldValue &&
						String(fieldValue)
							.toLowerCase()
							.endsWith(String(filter.value).toLowerCase())
					)

				case "is_empty":
					return fieldValue === undefined || fieldValue === null || fieldValue === ""

				case "is_not_empty":
					return fieldValue !== undefined && fieldValue !== null && fieldValue !== ""

				case "greater_than":
					return parseInt(fieldValue) > parseInt(filter.value)

				case "less_than":
					return parseInt(fieldValue) < parseInt(filter.value)

				case "in_list":
					// Handle visit status filter specially by checking internalStatus field
					if (filter.property === "visitStatus") {
						return (
							filter.values &&
							filter.values.length > 0 &&
							filter.values.includes(item.internalStatus)
						)
					}
					// Handle acuity specially as a number comparison
					else if (filter.property === "acuity") {
						return (
							filter.values &&
							filter.values.length > 0 &&
							filter.values.some(v => Number(fieldValue) === Number(v))
						)
					}
					return (
						filter.values &&
						filter.values.length > 0 &&
						filter.values.includes(fieldValue)
					)

				case "not_in_list":
					// Handle visit status filter specially by checking internalStatus field
					if (filter.property === "visitStatus") {
						return (
							!filter.values ||
							filter.values.length === 0 ||
							!filter.values.includes(item.internalStatus)
						)
					}
					// Handle acuity specially as a number comparison
					else if (filter.property === "acuity") {
						return (
							!filter.values ||
							filter.values.length === 0 ||
							!filter.values.some(v => Number(fieldValue) === Number(v))
						)
					}
					return (
						!filter.values ||
						filter.values.length === 0 ||
						!filter.values.includes(fieldValue)
					)

				default:
					return true
			}
		},
		[availableColumns, sandbox, isTomorrowMode]
	)

	// Initialize filtered data when patientsWithIssues changes
	useEffect(() => {
		setFilteredData(patientsWithIssues)
	}, [patientsWithIssues])

	// Apply filters when dependencies change
	useEffect(() => {
		filterData(query)
	}, [filterData, query])

	const handleSearch = e => {
		sharedHandleSearch(e, setQuery, filterData)
		// Reset pagination when search changes
		if (resetPaginationRef.current) {
			resetPaginationRef.current()
		}
	}

	const handleExportClick = () => {
		handleExport(filteredData, [], []) // Empty nurse and chair schedules for future patients
	}

	const handleColumnsChange = columns => {
		setVisibleColumns(columns)
	}

	// Handler for the advanced filters
	const handleApplyFilters = (filters, relationship = "AND") => {
		setActiveFilters(filters)
		setFilterRelationship(relationship)
		filterData(query) // Reapply filtering with the new filters

		// Reset pagination when advanced filters change
		if (resetPaginationRef.current) {
			resetPaginationRef.current()
		}
	}

	// Handler for sort configuration changes
	const handleSortConfigChange = (key, direction) => {
		const newConfig = { key, direction }
		setSortConfig(newConfig)
	}

	return (
		<div className="bg-white rounded-xl shadow p-4 h-full flex flex-col">
			<h3 className="text-lg font-bold mb-4">Scheduled Patients</h3>

			{/* Header with search, filters, and export - matching exact layout */}
			<div className="flex justify-between items-center py-4 bg-white sticky top-0 z-20">
				<div className="relative w-full max-w-xs flex items-center">
					<IoSearchOutline className="absolute left-3 text-gray-400" />
					<input
						type="text"
						className="
						sensitive ph-no-capture
						py-5 px-4 w-screen border border-transparent rounded-md pl-8 
						text-sm h-8 bg-gray-100 text-gray-600 placeholder-gray-500 
						outline-none focus:outline-none focus:ring-0 focus:border-customBlue"
						placeholder="Search by Patient Name"
						value={query}
						onChange={handleSearch}
					/>
				</div>
				<div className="flex items-center space-x-2">
					<ColumnSelector
						allColumns={availableColumns}
						activeColumns={
							visibleColumns ||
							availableColumns.filter(col => col.default).map(col => col.id)
						}
						onColumnsChange={handleColumnsChange}
					/>

					<PatientFilter
						columns={availableColumns}
						applyFilters={handleApplyFilters}
						nurseSchedule={[]}
						chairSchedule={[]}
						initialFilters={activeFilters}
						initialFilterRelationship={filterRelationship}
						patientSchedule={patientsWithIssues}
					/>

					<button
						className="py-2 px-4 bg-white border border-gray-300 text-gray-800 rounded-lg flex items-center gap-2 hover:bg-gray-100 focus:outline-none"
						onClick={handleExportClick}
					>
						<div className="flex items-center gap-1">
							<span className="inline-flex">
								<CiExport className="h-4 w-4 text-gray-600" />
							</span>
							<span className="font-medium">Export</span>
						</div>
					</button>
				</div>
			</div>

			<div className="overflow-x-auto">
				<PatientTableComponent
					filteredData={filteredData}
					visibleColumns={
						visibleColumns ||
						availableColumns.filter(col => col.default).map(col => col.id)
					}
					mode="future"
					schedulingIssues={schedulingIssues}
					nurseSchedule={[]}
					chairSchedule={[]}
					setNotification={() => {}}
					query={query}
					sortConfig={sortConfig}
					setSortConfig={handleSortConfigChange}
					onColumnsChange={handleColumnsChange}
					resetPaginationRef={resetPaginationRef}
					availableColumnsFromParent={availableColumns}
					isToday={false}
				/>
			</div>
		</div>
	)
}

export default FuturePatientList
